import { useEffect, useState } from "react";
/**
 * This Hook can be used for detecting key presses
 * @param {string} targetKey - The key to detect
 * @param {HTMLElement} dropDownListElement - The focused drop down element. Optional.
 * @returns {boolean} - Returns true if the key is pressed
 * @example
 * const isKeyPressed = useKeyPressOnElement('Enter', dropDownListElement);
 * useEffect(() => {
 *  if (isKeyPressed) {
 *   console.log('Enter key is pressed');
 * }
 * }, [isKeyPressed]);
 */
const useKeyPressOnElement = (
  targetKey: string,
  dropDownListElement: HTMLElement | null,
) => {
  const [keyPressed, setKeyPressed] = useState(false);
  const isFocused = () => dropDownListElement === document.activeElement;

  const downHandler = (event: KeyboardEvent) => {
    if (event.key === targetKey && isFocused()) {
      event.preventDefault();
      setKeyPressed(true);
    }
  };

  const upHandler = (event: KeyboardEvent) => {
    if (event.key === targetKey && isFocused()) {
      event.preventDefault();
      setKeyPressed(false);
    }
  };

  useEffect(() => {
    if (!dropDownListElement) {
      return;
    }
    dropDownListElement.addEventListener("keydown", downHandler);
    dropDownListElement.addEventListener("keyup", upHandler);

    return () => {
      dropDownListElement.removeEventListener("keydown", downHandler);
      dropDownListElement.removeEventListener("keyup", upHandler);
    };
  }, [targetKey, dropDownListElement]);

  return keyPressed;
};

export default useKeyPressOnElement;
